/*
Señal única de "la sesión venció", para que un 401 termine la sesión en vez de
reintentarse para siempre.

Por qué existe. El 2026-08-05 un solo navegador generó 121.376 peticiones en cuatro
horas —unas 10 por segundo— y las 121.376 fueron rechazadas con 401. Ninguna trajo
datos: era una pestaña abierta con el token vencido. El efecto de AuthProvider pedía
`/api/bodies` y `/api/branches/act/:org`, recibía 401, guardaba el resultado vacío, y
ese guardado volvía a disparar el mismo efecto. Un círculo sin pausa ni salida.

Faltaban las dos mitades del problema: el ciclo en sí (arreglado en AuthProvider) y el
hecho de que un 401 no cerraba la sesión. `utils/api.ts` sólo tenía interceptor de
request, y `customFetch` se limitaba a lanzar un Error como con cualquier otro estado.
Así, el token vencido nunca se resolvía y la pestaña quedaba golpeando la API hasta que
alguien la cerrara.

Acá centralizamos el aviso para que ambos clientes HTTP —axios y customFetch— hagan lo
mismo, y para que un aluvión de 401 simultáneos produzca un solo cierre de sesión.
*/

export const SESSION_EXPIRED_EVENT = 'veekls:session-expired'

/**
 * Header con el que la API marca "el 401 es porque TU sesión de Veekls no sirve".
 * Lo pone sólo el middleware de autenticación (api: utils/sessionExpired.ts), que es
 * lo único que sabe si el JWT falta, venció o es inválido.
 */
export const SESSION_EXPIRED_HEADER = 'X-Veekls-Session'

// Un 401 rara vez llega solo: la app dispara varias peticiones en paralelo y todas
// fallan a la vez. Sin este pestillo emitiríamos un evento por cada una y el cierre de
// sesión se ejecutaría muchas veces.
let alreadyNotified = false

/** El login responde 401 con credenciales incorrectas; eso no es una sesión vencida. */
const isAuthEndpoint = (url: string): boolean => /\/login(\/|\?|$)/.test(url)

/*
Antes bastaba con que el status fuera 401. Pero 401 es un número genérico: lo usan las
integraciones para decir "el tercero no me autorizó" y decenas de rutas de la API para
decir "no existe X". El 2026-08-06 abrir la pantalla de Mercado Libre en una
organización sin la integración conectada echaba al usuario de Veekls.

Ahora exigimos la marca de la API. Cualquier otro 401 es un error normal y no cierra
nada, así que una ruta nueva no puede volver a provocar esto sin querer.
*/
export const notifySessionExpired = (url: string, sessionExpired: boolean): void => {
  if (!sessionExpired) return

  if (isAuthEndpoint(url)) return

  if (alreadyNotified) return

  alreadyNotified = true
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
}

/** Se llama al iniciar sesión, para que un vencimiento posterior vuelva a avisar. */
export const resetSessionExpiredNotice = (): void => {
  alreadyNotified = false
}
